import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

const AccessibilityCtx = createContext(null);

const FONT_SIZES = [14, 16, 18, 20, 22];

// Persistent boolean helper
const persistBool = (key, fallback = false) => {
  if (typeof window === "undefined") return fallback;
  const v = window.localStorage.getItem(key);
  if (v === "1") return true;
  if (v === "0") return false;
  return fallback;
};

const persistInt = (key, fallback = 0) => {
  if (typeof window === "undefined") return fallback;
  const v = window.localStorage.getItem(key);
  return v != null ? parseInt(v, 10) || 0 : fallback;
};

const persistStr = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  return window.localStorage.getItem(key) || fallback;
};

export function AccessibilityProvider({ children }) {
  // Existing
  const [fontSizeIdx, setFontSizeIdx] = useState(() => persistInt("alpha_font_idx", 1));
  const [contrast, setContrast] = useState(() => persistBool("alpha_contrast"));
  const [simple, setSimple] = useState(() => persistBool("alpha_simple"));
  const [lang, setLang] = useState(() => persistStr("alpha_lang", "en"));

  // New WCAG-friendly toggles
  const [reducedMotion, setReducedMotion] = useState(() => persistBool("alpha_reduced_motion"));
  const [dyslexia, setDyslexia] = useState(() => persistBool("alpha_dyslexia"));
  const [largeCursor, setLargeCursor] = useState(() => persistBool("alpha_large_cursor"));
  const [pausedAnims, setPausedAnims] = useState(() => persistBool("alpha_paused_anims"));
  const [underlineLinks, setUnderlineLinks] = useState(() => persistBool("alpha_underline_links"));
  const [textSpacing, setTextSpacing] = useState(() => persistBool("alpha_text_spacing"));

  // Apply all toggles to <html> as classes + CSS vars
  useEffect(() => {
    document.documentElement.style.setProperty("--alpha-font-size", `${FONT_SIZES[fontSizeIdx]}px`);
    localStorage.setItem("alpha_font_idx", String(fontSizeIdx));
  }, [fontSizeIdx]);

  useEffect(() => {
    document.documentElement.classList.toggle("alpha-contrast", contrast);
    localStorage.setItem("alpha_contrast", contrast ? "1" : "0");
  }, [contrast]);

  useEffect(() => {
    localStorage.setItem("alpha_simple", simple ? "1" : "0");
  }, [simple]);

  useEffect(() => {
    localStorage.setItem("alpha_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    document.documentElement.classList.toggle("alpha-reduce-motion", reducedMotion);
    localStorage.setItem("alpha_reduced_motion", reducedMotion ? "1" : "0");
  }, [reducedMotion]);

  useEffect(() => {
    document.documentElement.classList.toggle("alpha-dyslexia", dyslexia);
    localStorage.setItem("alpha_dyslexia", dyslexia ? "1" : "0");
  }, [dyslexia]);

  useEffect(() => {
    document.documentElement.classList.toggle("alpha-large-cursor", largeCursor);
    localStorage.setItem("alpha_large_cursor", largeCursor ? "1" : "0");
  }, [largeCursor]);

  useEffect(() => {
    document.documentElement.classList.toggle("alpha-pause-anims", pausedAnims);
    localStorage.setItem("alpha_paused_anims", pausedAnims ? "1" : "0");
  }, [pausedAnims]);

  useEffect(() => {
    document.documentElement.classList.toggle("alpha-underline-links", underlineLinks);
    localStorage.setItem("alpha_underline_links", underlineLinks ? "1" : "0");
  }, [underlineLinks]);

  useEffect(() => {
    document.documentElement.classList.toggle("alpha-text-spacing", textSpacing);
    localStorage.setItem("alpha_text_spacing", textSpacing ? "1" : "0");
  }, [textSpacing]);

  const reset = () => {
    setFontSizeIdx(1);
    setContrast(false);
    setSimple(false);
    setLang("en");
    setReducedMotion(false);
    setDyslexia(false);
    setLargeCursor(false);
    setPausedAnims(false);
    setUnderlineLinks(false);
    setTextSpacing(false);
  };

  const value = useMemo(
    () => ({
      // Font
      fontSizeIdx,
      setFontSizeIdx,
      fontLabel: ["A--", "A-", "A", "A+", "A++"][fontSizeIdx],
      bumpFont: () => setFontSizeIdx((i) => Math.min(FONT_SIZES.length - 1, i + 1)),
      dropFont: () => setFontSizeIdx((i) => Math.max(0, i - 1)),
      // Modes
      contrast,
      setContrast,
      simple,
      setSimple,
      lang,
      setLang,
      reducedMotion,
      setReducedMotion,
      dyslexia,
      setDyslexia,
      largeCursor,
      setLargeCursor,
      pausedAnims,
      setPausedAnims,
      underlineLinks,
      setUnderlineLinks,
      textSpacing,
      setTextSpacing,
      reset,
    }),
    [fontSizeIdx, contrast, simple, lang, reducedMotion, dyslexia, largeCursor, pausedAnims, underlineLinks, textSpacing]
  );

  return <AccessibilityCtx.Provider value={value}>{children}</AccessibilityCtx.Provider>;
}

export const useAccessibility = () => {
  const ctx = useContext(AccessibilityCtx);
  if (!ctx) throw new Error("useAccessibility must be used inside AccessibilityProvider");
  return ctx;
};
